import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { NotificationButton } from '@avanta/shared/components/chat/NotificationButton';
import { APPOINTMENT_REMOTE_ID } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { MessageCircle } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ChatStartConfirmDialog } from 'components/ChatStartConfirmDialog';
import { ROUTES } from 'constants/routes';
import { AppointmentType, type Center, type RecentPsychologist } from 'types/appointments';
import { getCenterName } from 'utils/appointments';
import { BookButton } from './BookButton';
import { useFetchRecentPsychologists } from './useFetchRecentPsychologists';

const EMPTY_CELL = '-';

const getCenterCell = (center: Center | undefined): string => {
  if (!center) return EMPTY_CELL;
  if (center.id === APPOINTMENT_REMOTE_ID) {
    return t('AppointmentCard.types.remote', { ns: LOCIZE_NS.APPOINTMENTS });
  }
  return getCenterName(AppointmentType.InPerson, center);
};

export const RecentPsychologists = (): React.ReactElement | null => {
  const { data: recentPsychologists, isPending } = useFetchRecentPsychologists();
  const isInitialLoading = isPending && !recentPsychologists;
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { showDialog } = useDialogContext();
  const navigate = useNavigate();

  const handleStartChat = useCallback(
    (psychologistId: string): void => {
      showDialog({
        component: (
          <ChatStartConfirmDialog
            onConfirm={() => navigate(ROUTES.CHAT, { state: { psychologistId } })}
          />
        ),
      });
    },
    [navigate, showDialog]
  );

  const columns: ColumnDef<RecentPsychologist>[] = useMemo(
    () => [
      {
        id: 'psychologist',
        header: t('AppointmentsTable.psychologist.label'),
        accessorFn: row => `${row.psychologist.name} ${row.psychologist.surname}`,
        cell: ({ row }) => `${row.original.psychologist.name} ${row.original.psychologist.surname}`,
      },
      {
        id: 'speciality',
        header: t('RecentPsychologists.Speciality.heading'),
        accessorFn: row => t(`AppointmentPdf.Speciality.options.${row.specialitiesBookable}`),
        cell: ({ row }) =>
          t(`AppointmentPdf.Speciality.options.${row.original.specialitiesBookable}`),
      },
      {
        id: 'center',
        header: t('AppointmentsTable.center.label'),
        cell: ({ row }) => getCenterCell(row.original.center),
        accessorFn: row => getCenterCell(row.center),
      },
      {
        id: 'actions',
        header: t('AppointmentsTable.actions.label'),
        cell: ({ row }) => (
          <div className="flex gap-2 max-am-lg:flex-col">
            <BookButton recentPsychologist={row.original} />
            <NotificationButton
              variant="terciary"
              onClick={() => handleStartChat(row.original.psychologist.id)}
              icon="leading"
              notificationUserId={row.original.psychologist.id}
            >
              <MessageCircle className="size-4" />
              {t('RecentPsychologists.startChat.cta')}
            </NotificationButton>
          </div>
        ),
      },
    ],
    [t, handleStartChat]
  );

  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  if (!recentPsychologists?.length) {
    return null;
  }

  return (
    <ClientPaginatedTable
      data={recentPsychologists}
      columns={columns}
      withSearch
      title={t('RecentPsychologists.heading')}
      subtitle={t('RecentPsychologists.results', {
        ns: LOCIZE_NS.APPOINTMENTS,
        totalCount: recentPsychologists.length,
      })}
    />
  );
};
